import { Location } from '@angular/common';
import { Component, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BulkSelectEmployeeDialogComponent, DeprecatedConfirmationDialogComponent } from '@app/components';
import { BenefitShowMenuService } from '@app/modules/benefits/services/benefits-menu.service';
import { AuthService } from '@app/services/auth.service';
import { FileHelperService } from '@app/services/file-helper.service';
import { NotifyService } from '@app/services/notify.service';
import { BenefitsResolver } from '@benefits/benefits.resolver';
import { ShowView } from '@common/views';
import { BenefitPlan } from '@models/benefits/benefit-plan.model';
import { Benefit } from '@models/benefits/benefit.model';

@Component({
    templateUrl: './plan-show.template.html',
    styleUrls: ['./plan-show.style.scss'],
})
export class PlanShowView extends ShowView {
    @ViewChild('deleteConfirmationDialog', { static: true })
    deleteConfirmationDialog: DeprecatedConfirmationDialogComponent;
    @ViewChild('deleteBenefitConfirmationDialog', { static: true })
    deleteBenefitConfirmationDialog: DeprecatedConfirmationDialogComponent;
    @ViewChild('deleteBenefitDetailConfirmationDialog')
    deleteBenefitDetailConfirmationDialog: DeprecatedConfirmationDialogComponent;
    @ViewChild('selectEmployees') selectEmployees: BulkSelectEmployeeDialogComponent;

    isLoading = false;
    benefitPlan: BenefitPlan;
    settings: any;

    canModifyBenefits = false;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private location: Location,
        private notify: NotifyService,
        private auth: AuthService,
        public file: FileHelperService,
        private resolver: BenefitsResolver,
        public benefitShowMenuService: BenefitShowMenuService
    ) {
        super();
        this.benefitPlan = this.route.snapshot.data.models.get('benefitPlan');
        this.canModifyBenefits = !this.benefitPlan?.benefitPolicy?.hasEnhancedManagedBenefits;
    }

    onSave(): void {}

    onCancel(): void {}

    onDelete(): void {}

    resolve(): void {
        this.isLoading = true;
        this.resolver.resolve(this.route.snapshot, this.router.routerState.snapshot).then((models) => {
            this.benefitPlan = <BenefitPlan>models.get('benefitPlan');

            this.isLoading = false;
        });
    }

    onSaveBenefitPlan(): void {
        this.isLoading = true;
        this.benefitPlan
            .param('company', this.auth.company.id)
            .save()
            .then((benefitPlan) => {
                this.benefitPlan = benefitPlan;
                this.resolve();
                this.notify.success('saved');
            })
            .catch((err) => {
                this.isLoading = false;
                this.notify.error(err.message);
            });
    }

    onCancelBenefitPlan(): void {
        this.benefitPlan.revert();
    }

    onDeleteBenefitPlan(): void {
        this.deleteConfirmationDialog
            .show()
            .then((confirmDelete) => {
                if (confirmDelete) {
                    this.isLoading = true;
                    this.benefitPlan
                        .delete()
                        .then(() => {
                            this.location.back();
                            this.notify.success('Deleted');
                        })
                        .catch(() => {
                            this.notify.error('Could not delete feedback.');
                            this.isLoading = false;
                        });
                }
            })
            .catch(() => {});
    }

    onDeleteBenefit(benefit: Benefit) {
        this.deleteBenefitConfirmationDialog
            .show()
            .then((confirmDelete) => {
                if (confirmDelete) {
                    this.isLoading = true;
                    benefit
                        .param('company', this.auth.company.id)
                        .param('benefitPlan', this.benefitPlan.id)
                        .delete()
                        .then(() => {
                            this.resolve();
                            this.notify.success('Deleted');
                        })
                        .catch((err) => {
                            this.notify.error(err.message);
                            this.isLoading = false;
                        });
                }
            })
            .catch(() => {});
    }

    onDownloadPlan(plan: BenefitPlan): void {
        this.file.save(plan.fileId, plan.name, '.pdf');
    }

    onDownloadBenefit(benefitPlan: BenefitPlan, benefit: Benefit): void {
        const fileName = benefitPlan.name + (benefit.benefitCategory ? ' - ' + benefit.benefitCategory.name : '');

        this.file.save(benefit.fileId, fileName, '.pdf');
    }
}
